import { Component, EventEmitter, Input, OnInit, Output } from '@angular/core';
import {
  CostReportIncludeOptions,
  CostReportSortOptions,
} from '@inventory/models/cost-report-model.model';
import { CostComparisonModalOutputUI } from '@inventory/models/inventory-view.model';
import { RadioBtnInventory } from '@inventory/models/radio-btn-inventory.model';
import { TranslateService } from '@ngx-translate/core';
import { PlatformEnum, PlatformService } from '@panamax/app-state';
import { RightPaneComponent } from '@shared/components/dual-pane/right-side/right-pane/right-pane.component';
import { BaseDualPaneModalContainerComponent } from '@shared/components/modal-with-nav-inline/dual-pane-container/base-dual-pane-modal-container/base-dual-pane-modal-container.component';
import {
  dualPaneBasicSelection,
  dualPaneDefaultVerifySave,
  getSearchableObservable$,
  radioBtnInventorySearchDefault,
  radioBtnInventorySelection,
  singleSelectRadioSearchDefault,
  updatePaneDirectlyWithValue,
  updateRightPaneWithValue,
  updateRightPaneWithValueForMaps,
} from '@shared/components/modal-with-nav-inline/dual-pane-container/Helpers/dual-pane-helper-functions';
import { DualPaneView } from '@shared/constants/dual-pane.enum';
import {
  BasicListItem,
  DualPaneFlagsConfig,
  DualPaneFunctionsConfig,
  DualPaneLabelsConfig,
  DualPaneViewModel,
  RightPaneInfo,
  SelectableItem,
} from '@shared/models/dual-pane-model';
import { BehaviorSubject, map, Observable, of } from 'rxjs';

@Component({
  selector: 'app-cost-comparison-report-container',
  templateUrl: './cost-comparison-report-container.component.html',
  styleUrls: ['./cost-comparison-report-container.component.scss'],
})
export class CostComparisonReportContainerComponent extends BaseDualPaneModalContainerComponent {
  @Input() completedInventories$: Observable<RadioBtnInventory[]>;
  @Input() glFlag: boolean;

  constructor(
    public platformService: PlatformService,
    public translateService: TranslateService,
  ) {
    super(platformService);
  }

  createVmForCostComparison() {
    const labelConfig: DualPaneLabelsConfig = {
      dataCy: 'mass-delete-modal',
      title: 'i18n.inventory.downloadCostComparisonReport',
      complexSubTitle: [
        {
          text: 'i18n.inventory.costComparisonReportModal.firstHalfSubTitle',
          isBold: false,
        },
        {
          text: 'i18n.inventory.costComparisonReportModal.completed',
          isBold: true,
        },
        {
          text: 'i18n.inventory.costComparisonReportModal.secondHalfSubTitle',
          isBold: false,
        },
      ],
      submitText: this.translateService.instant('i18n.common.download'),
      backText: this.translateService.instant('i18n.common.save'),
    };
    const functionConfig: DualPaneFunctionsConfig = {
      intialDesktopTabletView: this.buildInventorySelectionPane1,
      handleSubmit: this.handleSubmitValues,
      verifySubmit: this.costComparisonVerifySubmit,
    };

    const flagConfig: DualPaneFlagsConfig = {};

    const contentItems: BasicListItem[] = [
      {
        label: 'i18n.inventory.costComparisonReportModal.selectFirstInventory',
        subLabel:
          'i18n.inventory.costComparisonReportModal.selectInventoryToCompare',
        view: DualPaneView.searchableInventoryRadio,
        selected: this.platformService.platformType !== PlatformEnum.mobile,
        onSelectfunction: this.buildInventorySelectionPane1,
      },
      {
        label: 'i18n.inventory.costComparisonReportModal.selectSecondInventory',
        subLabel:
          'i18n.inventory.costComparisonReportModal.selectInventoryToCompare',
        view: DualPaneView.searchableInventoryRadio,
        selected: false,
        onSelectfunction: this.buildInventorySelectionPane2,
      },
      {
        label: 'i18n.common.selectFormat',
        subLabel: 'PDF',
        view: DualPaneView.searchableItemRadio,
        selected: false,
        onSelectfunction: this.buildFormatSelectionPane,
        value: 'PDF',
      },
      {
        label: 'i18n.common.sortBy',
        subLabel: CostReportSortOptions.alphabeticalAsc,
        view: DualPaneView.searchableItemRadio,
        selected: false,
        onSelectfunction: this.buildSortSelectionPane,
        value: CostReportSortOptions.alphabeticalAsc,
      },
      {
        label: 'i18n.common.include',
        subLabel: CostReportIncludeOptions.allProducts,
        view: DualPaneView.searchableItemRadio,
        selected: false,
        onSelectfunction: this.buildIncludePane,
        value: CostReportIncludeOptions.allProducts,
      },
    ];

    if (this.glFlag) {
      contentItems.push({
        label: 'i18n.inventory.costReportModal.includeGlCodes',
        subLabel: 'nope',
        view: DualPaneView.noView,
        selected: false,
        onSelectfunction: () => {},
        value: false,
        isCheckbox: true,
      });
    }

    this.buildBasePaneWithConfigs(
      labelConfig,
      functionConfig,
      flagConfig,
      contentItems,
    );
  }

  async openModal() {
    this.createVmForCostComparison();
    this.isModalOpen$.next(true);
  }

  closeModal() {
    this.isModalOpen$.next(false);
  }

  buildInventorySelectionPane1 = (
    viewModel: DualPaneViewModel,
    paneSelected: string,
  ) => {
    let leftValue;
    let altValue;
    if (!!viewModel?.leftView) {
      leftValue = viewModel.leftView.content[0].value;
      altValue = viewModel.leftView.content[1].value;
    }
    const inventoriesBarringSecondSelection$ = this.getInventoriesObservable$(
      altValue,
      leftValue,
    );

    const inventorySelection1 = {
      view: DualPaneView.searchableInventoryRadio,
      title: 'i18n.inventory.costComparisonReportModal.selectFirstInventory',
      searchPane: {
        searchPlaceholder: 'i18n.inventory.searchForInventory',
        onSearchFunction: radioBtnInventorySearchDefault,
        searchBehavior: new BehaviorSubject<string>(''),
        searchKey: '',
      },
      itemsObservable$: inventoriesBarringSecondSelection$,
      handleItemSelection: radioBtnInventorySelection,
      verifySaveButton: dualPaneDefaultVerifySave,
      overrideSaveOnNavigation: this.touchInventorySelectionOnBackButton,
    } as RightPaneInfo;

    inventorySelection1.searchPane.searchableObservable$ =
      getSearchableObservable$(inventorySelection1);

    updatePaneDirectlyWithValue(leftValue, inventorySelection1);
    if (!!leftValue) {
      inventorySelection1.selectedItem = leftValue?.inventoryId;
    }
    return inventorySelection1;
  };

  buildInventorySelectionPane2 = (
    viewModel: DualPaneViewModel,
    paneSelected: string,
  ) => {
    let leftValue;
    let altValue;
    if (!!viewModel?.leftView) {
      leftValue = viewModel.leftView.content[1].value;
      altValue = viewModel.leftView.content[0].value;
    }

    const inventoriesBarringFirstSelection$ = this.getInventoriesObservable$(
      altValue,
      leftValue,
    );

    const inventorySelection2 = {
      view: DualPaneView.searchableInventoryRadio,
      title: 'i18n.inventory.costComparisonReportModal.selectSecondInventory',
      searchPane: {
        searchPlaceholder: 'i18n.inventory.searchForInventory',
        onSearchFunction: radioBtnInventorySearchDefault,
        searchBehavior: new BehaviorSubject<string>(''),
        searchKey: '',
      },
      itemsObservable$: inventoriesBarringFirstSelection$,
      handleItemSelection: radioBtnInventorySelection,
      verifySaveButton: dualPaneDefaultVerifySave,
      overrideSaveOnNavigation: this.touchInventorySelectionOnBackButton,
    } as RightPaneInfo;

    inventorySelection2.searchPane.searchableObservable$ =
      getSearchableObservable$(inventorySelection2);

    updatePaneDirectlyWithValue(leftValue, inventorySelection2);
    if (!!leftValue) {
      inventorySelection2.selectedItem = leftValue?.inventoryId;
    }
    return inventorySelection2;
  };

  getInventoriesObservable$ = (alternativeValue: any, currentValue: any) => {
    return this.completedInventories$.pipe(
      map(inventories =>
        inventories
          .filter(
            inventory =>
              inventory.inventoryId !== alternativeValue?.inventoryId,
          )
          .map(inventory => {
            return {
              ...inventory,
              selected: !!currentValue
                ? inventory.inventoryId == currentValue.inventoryId
                : false,
            };
          }),
      ),
    );
  };

  buildFormatSelectionPane = (
    viewModel: DualPaneViewModel,
    paneSelected: string,
  ) => {
    const formatItems = [
      {
        label: 'PDF',
        value: 'PDF',
        selected: true,
      } as SelectableItem,
      {
        label: 'CSV',
        value: 'CSV',
        selected: false,
      } as SelectableItem,
    ];
    const formatSelectionPane = {
      view: DualPaneView.searchableItemRadio,
      title: 'i18n.common.selectFormat',
      searchPane: {
        hideSearch: true,
        searchPlaceholder: '',
        searchBehavior: new BehaviorSubject<string>(''),
        searchKey: '',
        onSearchFunction: singleSelectRadioSearchDefault,
      },
      itemsObservable$: of(formatItems),
      handleItemSelection: dualPaneBasicSelection,
      verifySaveButton: dualPaneDefaultVerifySave,
    } as RightPaneInfo;

    formatSelectionPane.searchPane.searchableObservable$ =
      getSearchableObservable$(formatSelectionPane);

    updateRightPaneWithValue(viewModel, paneSelected, formatSelectionPane);
    return formatSelectionPane;
  };

  buildSortSelectionPane = (
    viewModel: DualPaneViewModel,
    paneSelected: string,
  ) => {
    const sortItems = [
      {
        label: CostReportSortOptions.alphabeticalAsc,
        value: CostReportSortOptions.alphabeticalAsc,
        selected: true,
      } as SelectableItem,
      {
        label: CostReportSortOptions.percentPriceChange,
        value: CostReportSortOptions.percentPriceChange,
        selected: false,
      } as SelectableItem,
    ];
    const sortSelectionPane = {
      view: DualPaneView.searchableItemRadio,
      title: 'i18n.common.selectFormat',
      searchPane: {
        hideSearch: true,
        searchPlaceholder: '',
        searchBehavior: new BehaviorSubject<string>(''),
        searchKey: '',
        onSearchFunction: singleSelectRadioSearchDefault,
      },
      itemsObservable$: of(sortItems),
      handleItemSelection: dualPaneBasicSelection,
      verifySaveButton: dualPaneDefaultVerifySave,
    } as RightPaneInfo;

    sortSelectionPane.searchPane.searchableObservable$ =
      getSearchableObservable$(sortSelectionPane);

    updateRightPaneWithValue(viewModel, paneSelected, sortSelectionPane);
    return sortSelectionPane;
  };

  buildIncludePane = (viewModel: DualPaneViewModel, paneSelected: string) => {
    const formatItems = [
      {
        label: CostReportIncludeOptions.allProducts,
        value: CostReportIncludeOptions.allProducts,
        selected: true,
      } as SelectableItem,
      {
        label: CostReportIncludeOptions.productsWithPriceChanges,
        value: CostReportIncludeOptions.productsWithPriceChanges,
        selected: false,
      } as SelectableItem,
    ];
    const includePane = {
      view: DualPaneView.searchableItemRadio,
      title: 'i18n.common.selectFormat',
      searchPane: {
        hideSearch: true,
        searchPlaceholder: '',
        searchBehavior: new BehaviorSubject<string>(''),
        searchKey: '',
        onSearchFunction: singleSelectRadioSearchDefault,
      },
      itemsObservable$: of(formatItems),
      handleItemSelection: dualPaneBasicSelection,
      verifySaveButton: dualPaneDefaultVerifySave,
    } as RightPaneInfo;

    includePane.searchPane.searchableObservable$ =
      getSearchableObservable$(includePane);

    updateRightPaneWithValue(viewModel, paneSelected, includePane);
    return includePane;
  };

  touchInventorySelectionOnBackButton = (
    viewModel: DualPaneViewModel,
    leftPaneItem: BasicListItem,
    value: RadioBtnInventory,
  ) => {
    leftPaneItem.value = value;
    leftPaneItem.subLabel = value.inventoryName;
    return leftPaneItem;
  };

  costComparisonVerifySubmit = (vm: DualPaneViewModel) => {
    // just need to ensure two inventories have been selected
    return !!vm.leftView.content[0]?.value && !!vm.leftView.content[1]?.value;
  };

  handleSubmitValues = (vm: DualPaneViewModel) => {
    const costComparisonOutput: CostComparisonModalOutputUI = {
      firstSelectedInventory: vm.leftView.content[0]?.value.inventoryId,
      secondSelectedInventory: vm.leftView.content[1]?.value.inventoryId,
      selectedFormat: vm.leftView.content[2]?.value,
      selectedSort: vm.leftView.content[3]?.value,
      productsToInclude: vm.leftView.content[4]?.value,
      includeGlCodes: vm.leftView.content[5]?.value,
    };
    this.closeModalAndSubmit(costComparisonOutput);
  };

  closeModalAndSubmit = (costComparisonOutput: CostComparisonModalOutputUI) => {
    this.isModalOpen$.next(false);
    if (costComparisonOutput !== undefined) {
      this.submitEvent.emit(costComparisonOutput);
    }
  };
}
